import React, { createContext, useCallback, useContext, useId } from 'react'
import { cn } from '@/lib/cn'

interface TabsContextValue {
  value: string
  onValueChange: (value: string) => void
  triggerIdPrefix: string
  panelIdPrefix: string
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext)
  if (ctx == null) {
    throw new Error('Tabs components must be used within Tabs')
  }
  return ctx
}

export interface TabsProps {
  value: string
  onValueChange: (value: string) => void
  className?: string
  children: React.ReactNode
}

export function Tabs({ value, onValueChange, className, children }: TabsProps): React.ReactElement {
  const baseId = useId()
  const triggerIdPrefix = `tabs-trigger-${baseId.replace(/:/g, '')}`
  const panelIdPrefix = `tabs-panel-${baseId.replace(/:/g, '')}`

  const ctx: TabsContextValue = {
    value,
    onValueChange,
    triggerIdPrefix,
    panelIdPrefix,
  }

  return (
    <TabsContext.Provider value={ctx}>
      <div className={cn('w-full', className)} data-state={value}>
        {children}
      </div>
    </TabsContext.Provider>
  )
}

Tabs.displayName = 'Tabs'

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function TabsList({ className, children, ...rest }: TabsListProps): React.ReactElement {
  return (
    <div
      role="tablist"
      className={cn(
        'flex flex-wrap gap-0 border-b border-gray-200 pb-px dark:border-gray-700',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

TabsList.displayName = 'TabsList'

export interface TabsTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  value: string
  children: React.ReactNode
}

export function TabsTrigger({
  value,
  className,
  children,
  id: idProp,
  ...rest
}: TabsTriggerProps): React.ReactElement {
  const { value: selectedValue, onValueChange, triggerIdPrefix, panelIdPrefix } = useTabsContext()
  const isSelected = selectedValue === value
  const triggerId = idProp ?? `${triggerIdPrefix}-${value}`
  const panelId = `${panelIdPrefix}-${value}`

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onValueChange(value)
      }
    },
    [onValueChange, value]
  )

  return (
    <button
      type="button"
      role="tab"
      id={triggerId}
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={isSelected ? 0 : -1}
      className={cn(
        'flex shrink-0 items-center rounded-t-lg px-5 py-3.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
        isSelected
          ? 'text-primary dark:text-primary'
          : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100',
        className
      )}
      onClick={() => onValueChange(value)}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      <span
        className={cn(
          'inline-block border-b-2 pb-0.5',
          isSelected ? 'border-primary' : 'border-transparent'
        )}
      >
        {children}
      </span>
    </button>
  )
}

TabsTrigger.displayName = 'TabsTrigger'

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  children: React.ReactNode
}

export function TabsContent({
  value,
  className,
  children,
  id: idProp,
  ...rest
}: TabsContentProps): React.ReactElement {
  const { value: selectedValue, triggerIdPrefix, panelIdPrefix } = useTabsContext()
  const isSelected = selectedValue === value
  const panelId = idProp ?? `${panelIdPrefix}-${value}`
  const triggerId = `${triggerIdPrefix}-${value}`

  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={triggerId}
      aria-hidden={!isSelected}
      hidden={!isSelected}
      tabIndex={isSelected ? 0 : -1}
      className={cn('pt-4', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

TabsContent.displayName = 'TabsContent'
